import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Auth, confirmPasswordReset, SAMLAuthProvider, sendPasswordResetEmail, signInWithEmailAndPassword, signInWithPopup } from '@angular/fire/auth';
import { Router } from '@angular/router';
import jwt_decode from 'jwt-decode';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AdminRoutes, Route, Routes, SpecialistRoutes } from '../core/routes';
import { HeaderService } from '../core/services/header/header.service';
import { Roles, User } from '../features/user/user';
import { ModalService } from '../shared/services/modal.service';
import { FIREBASE_MESSAGES, GENERIC_ERROR } from '../util/constantes-mensajes';
import { FirebaseAuth, Token, UserLogin } from './user-login';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private currentUserSubject: BehaviorSubject<User | null>;
    public currentUser$: Observable<User | null>;

    constructor(private router: Router, private http: HttpClient, private firebaseAuth: Auth, private modalService: ModalService, private headerService: HeaderService) {
        this.currentUserSubject = new BehaviorSubject<User | null>(null);
        this.currentUser$ = this.currentUserSubject.asObservable();
    }

    /**
     * This function gets current user data
     * @returns {User}
     */
    get user(): User | null {
        return this.currentUserSubject.getValue();
    }

    loginWithSAML() {
        const provider = new SAMLAuthProvider('saml.cyberark-identity');
        signInWithPopup(this.firebaseAuth, provider)
            .then(async (result) => {
                try {
                    const idToken = await result.user.getIdToken();
                    const refreshToken = result.user.refreshToken;
                    const authData = { idToken, refreshToken, expiresIn: '3600' } as FirebaseAuth;

                    await Promise.resolve(this.handleAuthentication(authData));
                    await this.router.navigateByUrl('/home');

                    return null;
                } catch (err) {
                    AuthService.handleError(err as HttpErrorResponse);
                    throw err;
                }
            })
            .catch((error) => {
                console.error('Error during SAML redirect:', error);
            });
    }

    public loginEmailPass(form: { email: string; password: string }): Promise<FirebaseAuth | null> {
        return signInWithEmailAndPassword(this.firebaseAuth, form.email, form.password)
            .then(async (userCredential) => {
                try {
                    const idToken = await userCredential.user.getIdToken();
                    const refreshToken = userCredential.user.refreshToken;
                    const authData = { idToken, refreshToken, expiresIn: '3600' } as FirebaseAuth;

                    await Promise.resolve(this.handleAuthentication(authData));

                    await this.router.navigateByUrl('/home');

                    return authData;
                } catch (err) {
                    AuthService.handleError(err as HttpErrorResponse);
                    throw err;
                }
            })
            .catch((error) => {


                let errorMessage = 'Error desconocido';

                switch (error.code) {
                    case 'auth/user-disabled':
                        errorMessage = 'El usuario está deshabilitado';
                        break;
                    case 'auth/invalid-email':
                        errorMessage = 'El correo electrónico no es válido';
                        break;
                    case 'auth/user-not-found':
                        errorMessage = 'Usuario no encontrado';
                        break;
                    case 'auth/wrong-password':
                    case 'auth/invalid-credential':
                        errorMessage = 'Contraseña o usuario incorrecto';
                        break;
                    case 'auth/too-many-requests':
                        errorMessage = 'Demasiados intentos fallidos. Intenta más tarde';
                        break;
                    default:
                        errorMessage = 'Contraseña o usuario incorrecto';
                        break;
                }

                this.modalService.getConfirmacionModal({
                    message: errorMessage,
                    confirmTitle: 'Cerrar',
                    isError: true,
                });
                return null;
            });
    }
    sendEmailResetPasswordLib(email: string) {
        sendPasswordResetEmail(this.firebaseAuth, email)
            .then((result) => {
                console.log('reset result:', result);
            })
            .catch((error) => {
                console.error('Error reset:', error);
            });
    }

    resetPasswordLib(oobCode: string, newPassword: string) {
        confirmPasswordReset(this.firebaseAuth, oobCode, newPassword)
            .then((result) => {
                console.log('reset result:', result);
            })
            .catch((error) => {
                console.error('Error reset:', error);
            });
    }

    /**
     * This function gets user credentials and sets user data in localstorage
     * @param {string} email
     * @param {string} password
     * @returns {Observable<FirebaseAuth>}
     */
    login(email: string, password: string): Observable<FirebaseAuth> {
        const userLogin: UserLogin = { email, password, returnSecureToken: true };
        const { apiUrl, apiKey } = environment.firebaseConfig;
        return this.http.post<FirebaseAuth>(`${apiUrl}/accounts:signInWithPassword?key=${apiKey}`, userLogin).pipe(
            catchError((error) => AuthService.handleError(error, this.isLoggedIn())),
            tap((result) => {
                // skipcq: JS-0328
                this.handleAuthentication(result);
            }), catchError((error) => {
                AuthService.handleError(error);
                return throwError(() => error);
            })
        );
    }

    /**
     * This function refreshes token
     * @returns {Observable<FirebaseAuth>}
     */
    refreshToken(): Observable<FirebaseAuth> {
        const refreshToken = {
            refresh_token: this.user?.refreshToken,
            grant_type: 'refresh_token'
        };
        const { apiUrl, apiKey } = environment.firebaseConfig;

        return this.http
            .post<{
                id_token: string;
                expires_in: number;
                refresh_token: string;
            }>(`${apiUrl}/token?key=${apiKey}`, refreshToken)
            .pipe(
                catchError((error) => {
                    this.logout();
                    return throwError(() => error);
                }),
                map(result => {
                    // Convertimos expires_in (number) a string
                    const auth: FirebaseAuth = {
                        idToken: result.id_token,
                        expiresIn: result.expires_in.toString(),
                        refreshToken: result.refresh_token
                    };
                    return auth;
                }),
                tap((auth) => this.handleAuthentication(auth))
            );
    }

    /**
     * This function sends an email to reset password
     * @param {string} email
     * @returns {Observable<FirebaseAuth>}
     */
    sendEmailResetPassword(email: string): Observable<FirebaseAuth> {
        const resetPassword = { email, requestType: 'PASSWORD_RESET' };
        const { apiUrl, apiKey } = environment.firebaseConfig;
        return this.http.post<FirebaseAuth>(`${apiUrl}/accounts:sendOobCode?key=${apiKey}`, resetPassword).pipe(catchError((error) => AuthService.handleError(error)));
    }

    /**
     * This function sends an email to reset password
     * @param {string} oobCode
     * @param {string} newPassword
     * @returns {Observable<FirebaseAuth>}
     */
    resetPassword(oobCode: string, newPassword: string): Observable<FirebaseAuth> {
        const resetPassword = { oobCode, newPassword };
        const { apiUrl, apiKey } = environment.firebaseConfig;
        return this.http.post<FirebaseAuth>(`${apiUrl}/accounts:resetPassword?key=${apiKey}`, resetPassword).pipe(catchError((error) => AuthService.handleError(error)));
    }

    /**
     * This function sends an email to change password
     * @param {string} password
     * @returns {Observable<FirebaseAuth>}
     */
    changePassword(password: string): Observable<FirebaseAuth> {
        const request = { idToken: this.user?.token, password, email: this.user?.email, returnSecureToken: true };
        const { apiUrl, apiKey } = environment.firebaseConfig;
        return this.http.post<FirebaseAuth>(`${apiUrl}/accounts:update?key=${apiKey}`, request).pipe(
            catchError((error) => AuthService.handleError(error)),
            tap((result) => {
                // skipcq: JS-0328
                this.handleAuthentication(result);
            }), catchError((error) => {
                AuthService.handleError(error);
                return throwError(() => error);
            })
        );
    }

    /**
     * Decodes the JWT token
     * @param {string} token
     * @returns {Token}
     */
    private static decodeToken(token: string): Token {
        return jwt_decode(token);
    }

    /**
     * This function sets the session user to save in the localStorage
     * @param {FirebaseAuth} login
     * @returns {none}
     */
    private async handleAuthentication(login: FirebaseAuth): Promise<void> {
        const decodedToken: Token = AuthService.decodeToken(login.idToken);
        const email = decodedToken.email;

        let user: User = {
            nombre: decodedToken.name,
            apellido: decodedToken.lastName,
            email: decodedToken.email,
            activo: true,
            token: login.idToken,
            refreshToken: login.refreshToken,
            uuid: decodedToken.user_id,
            rol: decodedToken.role,
        };
        this.headerService.search.user = user.uuid;
        this.currentUserSubject.next(user);

        try {
            // Se consulta en el back el usuario para obtener los permisos de edición sobre los módulos
            type AuthResponse = {
                editPermissions: string;
                role: string;
                nombre?: string;
                apellido?: string;
            };
            const response = await firstValueFrom(
                this.http.get<AuthResponse>(`${environment.urlBase}/user/auth`, {
                    params: { email },
                }).pipe(
                    catchError((error: HttpErrorResponse) => {
                        if (error.error.error.message === 'USER_DISABLED') {
                            this.modalService.getConfirmacionModal({
                                message: 'El usuario está deshabilitado',
                                confirmTitle: 'Cerrar',
                                isError: true,
                            });
                        }
                        return throwError(() => new Error(error.message || 'Error en la consulta de autenticación'));
                    })
                )
            );
            user.editPermissions = response.editPermissions;
            // Si no hay rol en el token, se agrega data del backend
            if (!user.rol) {
                user = {
                    ...user,
                    rol: response.role,
                    nombre: response.nombre ?? user.nombre,
                    apellido: response.apellido ?? user.apellido,
                };
            }
        } catch (err) {
            this.currentUserSubject.next(null);
            localStorage.setItem('analiticaNovaUser', JSON.stringify(null));
            console.error('Error al obtener datos del usuario:', err);
            return;
        }
        this.currentUserSubject.next(user);
        localStorage.setItem('analiticaNovaUser', JSON.stringify(user));
    }

    /**
     * Gets the user routes by role
     * @returns {Route[]}
     */
    getUserRoutes(): Route[] {
        let routes;
        switch (this.user?.rol) {
            case Roles.admin:
                routes = AdminRoutes;
                break;
            case Roles.specialist:
                routes = SpecialistRoutes;
                break;
            default:
                routes = Routes;
                break;
        }
        return routes;
    }

    /**
     * This function handles error messages from firebase
     * @param {HttpErrorResponse} errorRes
     * @param {boolean} isLoggedIn
     * @returns {Observable<never>}
     */
    private static handleError(errorRes: HttpErrorResponse, isLoggedIn = false): Observable<never> {
        let errorMessage = GENERIC_ERROR;

        switch (errorRes.error.error.message) {
            case 'EMAIL_NOT_FOUND':
                errorMessage = FIREBASE_MESSAGES.ERROR_EMAIL;
                break;
            case 'INVALID_EMAIL':
                errorMessage = FIREBASE_MESSAGES.ERROR_INVALID_EMAIL;
                break;
            case 'INVALID_PASSWORD':
                errorMessage = isLoggedIn ? FIREBASE_MESSAGES.ERROR_PASSWORD : FIREBASE_MESSAGES.ERROR_EMAIL_AND_PASSWORD;
                break;
            case 'USER_DISABLED':
                errorMessage = FIREBASE_MESSAGES.ERROR_USER_DISABLED;
                break;
            case 'INVALID_OOB_CODE':
                errorMessage = FIREBASE_MESSAGES.ERROR_INVALID_OOB_CODE;
                break;
            case 'EXPIRED_OOB_CODE':
                errorMessage = FIREBASE_MESSAGES.ERROR_EXPIRED_OOB_CODE;
                break;
            case 'INVALID_REFRESH_TOKEN':
                errorMessage = FIREBASE_MESSAGES.ERROR_INVALID_REFRESH_TOKEN;
                break;
            default:
                break;
        }
        return throwError(() => errorMessage);
    }

    /**
     * Validates if the user data exists in localStorage to allow auto login
     */
    autoLogin() {
        try {
            const user: User = JSON.parse(localStorage.getItem('analiticaNovaUser') as string);
            if (user) {
                this.headerService.search.user = user.uuid;
                if (user.token) {
                    const decodedToken: Token = AuthService.decodeToken(user.token);
                    user.rol = decodedToken.role;
                }
                this.currentUserSubject.next(user);
            }
        } catch (error) {
            console.error('Error parsing user data from localStorage:', error);
            // Si hay error al parsear, limpiar localStorage
            localStorage.removeItem('analiticaNovaUser');
        }
    }

    /**
     * Validates if a user is logged in the application
     * @returns {boolean}
     */
    isLoggedIn(): boolean {
        const userData = this.currentUserSubject.getValue();
        return Boolean(userData);
    }

    /**
     * Removes user data and redirects to login page
     */
    logout(): void {
        this.currentUserSubject.next(null);
        localStorage.removeItem('analiticaNovaUser');
        localStorage.removeItem('chatMessages');
        // Remove all cookies
        document.cookie.split(';').forEach((c) => {
            const eqPos = c.indexOf('=');
            const name = eqPos > -1 ? c.substr(0, eqPos) : c;
            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
        });
        this.router.navigate(['auth']);
    }
}
